import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	FormControlLabel,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DirectionsRunRoundedIcon from '@mui/icons-material/DirectionsRunRounded';
import MedicationRoundedIcon from '@mui/icons-material/MedicationRounded';
import HeartBrokenRoundedIcon from '@mui/icons-material/HeartBrokenRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MainGrid from '../../components/MainGrid';
import { useNavigate } from 'react-router-dom';
import {
	getAccessToken,
	getElderHomeId,
	getUserEmail,
	getUserId,
	getUserName,
} from '../../Utils/AuthUtil';
import { useEffect, useMemo, useState } from 'react';
import { getEnabledUserFeatures } from '../../context/MainProvider';
import { FEATURE_TYPES } from '../../Utils/Enums';
import {
	fetchCQCReportData,
	generateNotificationData,
	getResidentIds,
} from '../../services/ElderService';
import { toast } from 'react-toastify';
import ToastBanner from '../../components/ToastBanner';
import { StarIcon } from '../../assets/icons/Icons';
import { helperText } from '../../assets/theme/Theme';
import { dark, surface, teal } from '../../assets/theme/Colors';

const initialErrorState = { status: false, msg: '' };

const templates = [
	{
		id: 'events_and_incidents.absence_of_registered_individual',
		title: 'Absence of Registered Individual',
		desc: 'Events & Incidents',
		icon: <WarningAmberRoundedIcon fontSize="small" />,
		instruction:
			'Please describe the absence/incident in detail, including the start date, reason, who will take over responsibilities, full details of the appointed person, and any additional information.',

		active: true,
	},
	{
		id: 'events_and_incidents.return_of_registered_individual',
		title: 'Return of Registered Individual',
		desc: 'Events & Incidents',
		icon: <DirectionsRunRoundedIcon fontSize="small" />,
		instruction:
			'Please describe the return from absence, including return date, original reference, and any additional details.',
	},
	{
		id: 'events_and_incidents.events_stop_service_running',
		title: 'Events: Stop Service Running',
		desc: 'Events & Incidents',
		icon: <MedicationRoundedIcon fontSize="small" />,
		instruction:
			'Please describe the event in detail, including the type of event, start date and time, reason or cause, impact on residents, staff, and service delivery, immediate actions taken, temporary arrangements made to keep the service running safely, details of any appointed person taking over responsibilities, and any further relevant information such as ongoing risks, external notifications, next steps, or preventive measures.',
	},
	{
		id: 'events_and_incidents.serious_injury',
		title: 'Serious Injury',
		desc: 'Events & Incidents',
		icon: <HeartBrokenRoundedIcon fontSize="small" />,
		instruction:
			'Please describe the serious injury incident in detail, including the date and time it occurred, where it happened, how the person was injured, the type and severity of injury, whether the person was already known to be at risk, what risk assessments or support measures were in place, immediate actions taken after the incident, medical treatment provided, whether family/relevant persons or authorities were informed, steps taken to reduce further risk, and any additional relevant information. Please use a unique identifier/code for the injured person and do not include their name.',
	},
];

const providerDetails = {
	providerNotificationReference: 'REF-ABS-2026-0018',
	providerName: 'Northfield Care Services Ltd',
	cqcProviderNumber: '1-0001234567',
	formFilledBy: '',
	dateSubmitted: '23/04/2026',
	contactForMoreInformation: 'Daniel Mercer',
	telephoneNumber: '01632 960421',
	emailAddress: '',
	affectedLocationScope: 'one_location',
	locationNameAndAddress:
		'Northfield Residential Home, 14 Willow Park Road, Ashford',
	locationPostcode: 'AS1 4PL',
	cqcLocationNumber: '1-0007654321',
};

export default function CQCNotification() {
	const navigate = useNavigate();
	const userId = getUserId();
	const homeId = getElderHomeId();
	const token = getAccessToken();
	const [id, setId] = useState(null);
	const [residentIds, setResidentIds] = useState([]);
	const [idError, setIdError] = useState(initialErrorState);
	const [tempError, setTempError] = useState(initialErrorState);
	// const [templates, setTemplates] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [selectedTemplateName, setSelectedTemplateName] = useState(null);
	const [tempInstructions, setTempInstructions] = useState([]);
	const [onFetchedPreFilledData, setOnFetchedPreFilledData] = useState(false);
	const [onShowTemplateResidentOpt, setOnShowTemplateResidentOpt] =
		useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { enabledUserFeatureList } = getEnabledUserFeatures();
	const isThisFeatureDisabled = !enabledUserFeatureList.includes(
		FEATURE_TYPES[3].key,
	);
	const [loadingMsg, setLoadingMsg] = useState('');
	const [isGenerationComplete, setIsGenerationComplete] = useState(false);
	const [residentFetchedIds, setResidentFetchedIds] = useState([]);
	const [showNoResidentBanner, setShowNoResidentBanner] = useState(false);
	const [fileData, setFileData] = useState([]);
	const [txtError, setTxtError] = useState(initialErrorState);
	const [anchorEl, setAnchorEl] = useState(null);
	const [instructions, setInstructions] = useState('');
	const [instructionError, setInstructionError] = useState(initialErrorState);
	const [reportData, setReportData] = useState({});

	useEffect(() => {
		fetchResidentIds();
		// const templateNames = TEMPLATES.map(item => item.displayName);
		// setTemplates(templateNames);
	}, []);

	async function fetchResidentIds() {
		const allResResponse = await getResidentIds(homeId, token);
		const response = await getResidentIds(homeId, token);

		if (allResResponse.message === 'Success') {
			setShowNoResidentBanner(
				allResResponse.content.length > 0 ? false : true,
			);
		}

		if (response.message === 'Success') {
			const newIds = response.content.map(item =>
				item.isFromExternalApi
					? item.name
					: `${item.elderId} (${item.name})`,
			);

			setResidentFetchedIds(response.content);
			setResidentIds(newIds);
		} else {
			toast.error(response.message);
		}
	}

	async function fetchTemplateData(docType) {
		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		const data = {
			elder_id: selectedElder.elderId,
			template_id: selectedTemplate,
			resolved_field_values: { ...reportData },
			return_download_docx: true,
			file_format: docType,
		};

		const tempTitle = templates.find(
			item => item.id === selectedTemplate,
		).title;

		const fileName = `${tempTitle} - ${selectedElder.name}`;

		return generateNotificationData(data, docType, fileName);
	}

	function validateUploadInputs() {
		let isValid = true;

		if (!id || id === '') {
			setIdError({ status: true, msg: 'Select Resident' });
			isValid = false;
		}

		if (!instructions || instructions === '') {
			setInstructionError({
				status: true,
				msg: 'Please Provide Details',
			});
			isValid = false;
		}

		return isValid;
	}

	async function getReportData() {
		if (!validateUploadInputs()) return;

		setLoadingMsg('Preparing Report...');
		setIsLoading(true);
		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		const d = new Date();
		const formatted = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

		const providerObj = {
			...providerDetails,
			formFilledBy: getUserName(),
			emailAddress: getUserEmail(),
			dateSubmitted: formatted,
		};

		const provider = Object.values(providerObj).join('\n');

		const body = {
			elder_id: selectedElder.id,
			template_id: selectedTemplate,
			input_text: instructions,
			client_id: getElderHomeId(),
			provider_details: provider,
		};

		const response = await fetchCQCReportData(body);
		setReportData(response.doc_json_loop.llm_filled_data_final || {});

		setIsLoading(false);
		setLoadingMsg('');
		setOnFetchedPreFilledData(true);
	}

	async function onFetchTemplateData(docType = 'docx') {
		// if (!validateUploadInputs()) return;

		// if (!instructions || instructions === '') {
		// 	setInstructionError({
		// 		status: true,
		// 		msg: 'Please Provide Details',
		// 	});
		// 	return;
		// }

		setAnchorEl(null);
		setLoadingMsg('Generating Document...');
		setIsLoading(true);

		try {
			const { blob, filename } = await fetchTemplateData(docType);

			downloadFile(blob, filename);

			setFileData([]);
			setId('');
			setSelectedTemplate('');
			setInstructions('');
			setTempInstructions([]);

			toast.success('Document downloaded!');
		} catch (error) {
			console.error(error);
			toast.error(error.message);
		} finally {
			setIsLoading(false);
			setLoadingMsg('');
		}
	}

	async function generateBothTempData() {
		// if (!validateUploadInputs()) return;

		// if (!instructions || instructions === '') {
		// 	setInstructionError({
		// 		status: true,
		// 		msg: 'Please Provide Details',
		// 	});
		// 	return;
		// }

		setAnchorEl(null);
		setLoadingMsg('Generating Document...');
		setIsLoading(true);

		try {
			const pdf = await fetchTemplateData('pdf');
			downloadFile(pdf.blob, pdf.filename);

			const docx = await fetchTemplateData('docx');
			downloadFile(docx.blob, docx.filename);

			setFileData([]);
			setId('');
			setSelectedTemplate('');
			setInstructions('');
			setTempInstructions([]);

			toast.success('Both documents downloaded!');
		} catch (error) {
			toast.error(error.message);
		} finally {
			setIsLoading(false);
			setLoadingMsg('');
		}
	}

	function downloadFile(blob, filename) {
		const url = URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = filename;

		document.body.appendChild(a);
		a.click();
		a.remove();

		URL.revokeObjectURL(url);
	}

	function onChangeSelection(e, value) {
		setSelectedTemplate(value.id);
		setSelectedTemplateName(value.title);
		setTempError(initialErrorState);
		onFetchInstructions(value.title);
		setOnShowTemplateResidentOpt(true);
	}

	function onChangeSelectionElder(value) {
		setId(value);
		setIdError(initialErrorState);
	}

	function onChangeInstruction(e) {
		setInstructions(e.target.value);
		setInstructionError(initialErrorState);
	}

	function onFetchInstructions(templateName) {
		const template = templates.find(item => item.title === templateName);
		// const response = fetchInstructionDescription(template.id);
		// if (!response.error) {
		setTempInstructions(template.instruction);
		// setOnShowTemplateInstruction(true);
		// } else {
		// 	toast.error('Unable to fetch instructions.');
		// }
	}

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0].size && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setTxtError({ status: false, msg: '' });
		setFileData([...fileData, e[0]]);
	}

	function onRemoveFiles(content) {
		const clone = fileData.filter(value => value.name !== content.name);
		setFileData(clone);
	}

	function handleClose() {
		setAnchorEl(null);
	}

	const filteredFields = useMemo(() => {
		const seen = new Set();

		return Object.entries(reportData).filter(([key]) => {
			const normalized = normalizeKey(key);

			if (seen.has(normalized)) {
				return false;
			}

			seen.add(normalized);
			return true;
		});
	}, [reportData]);

	function normalizeKey(key) {
		return key
			.toLowerCase()
			.replace(/\(\d+\)/g, '') // remove (2), (3)
			.replace(/[:]/g, '')
			.replace(/\s+/g, ' ')
			.trim()
			.replace(/\b\w/g, char => char.toUpperCase());
	}

	const handleChange = (key, value) => {
		setReportData(prev => ({
			...prev,
			[key]: value,
		}));
	};

	const textFieldStyle = {
		'& .MuiOutlinedInput-root': {
			fontSize: '14px',
			height: '40px',
			// Hide border by default
			'& fieldset': {
				border: 'none',
			},
			// Show border only on hover
			'&:hover fieldset': {
				border: '1px solid #e0e0e0',
			},
			// Keep focus border visible
			'&.Mui-focused fieldset': {
				border: '2px solid #1976d2', // or your preferred focus color
			},
		},
	};

	function onRenderFields() {
		return filteredFields.map(([key, value]) => {
			const isBoolean = typeof value === 'boolean';

			return (
				<Grid item xs={12} md={6} key={key}>
					<Box>
						<Typography
							sx={{
								fontSize: 12,
								fontWeight: 600,
								color: '#6b8888',
								mb: 0.7,
							}}>
							{normalizeKey(key)}
						</Typography>

						{isBoolean ? (
							<RadioGroup
								row
								value={reportData[key] === true ? 'yes' : 'no'}
								onChange={e =>
									handleChange(key, e.target.value === 'yes')
								}
								sx={{
									gap: 1,
									marginLeft: '5px',
								}}>
								<FormControlLabel
									value="yes"
									control={
										<Radio
											size="small"
											sx={{
												p: '4px',
												color: teal,
												'& .MuiSvgIcon-root': {
													fontSize: 18,
												},
												'&.Mui-checked': {
													color: teal,
												},
											}}
										/>
									}
									label="Yes"
									sx={{
										mr: 1,
										'& .MuiFormControlLabel-label': {
											fontSize: 13,
										},
									}}
								/>

								<FormControlLabel
									value="no"
									control={
										<Radio
											size="small"
											sx={{
												p: '4px',
												color: teal,
												'& .MuiSvgIcon-root': {
													fontSize: 18,
												},
												'&.Mui-checked': {
													color: teal,
												},
											}}
										/>
									}
									label="No"
									sx={{
										'& .MuiFormControlLabel-label': {
											fontSize: 13,
										},
									}}
								/>
							</RadioGroup>
						) : (
							<TextField
								fullWidth
								size="small"
								value={reportData[key] || ''}
								onChange={e =>
									handleChange(key, e.target.value)
								}
								multiline={
									String(reportData[key] || '').length > 80
								}
								minRows={
									String(reportData[key] || '').length > 80
										? 3
										: 1
								}
								sx={{
									'& .MuiOutlinedInput-root': {
										borderRadius: '10px',
										background: '#fff',
									},
									'& .MuiInputBase-input': {
										fontSize: 13,
									},
								}}
							/>
						)}
					</Box>
				</Grid>
			);
		});
	}

	return (
		<MainGrid>
			<Box
				sx={{
					width: '100%',
				}}>
				{/* Header */}
				<Box sx={{ mb: 3 }}>
					<Typography
						sx={{
							fontSize: '22px',
							fontWeight: 600,
							color: dark,
							mb: 0.5,
						}}>
						CQC Notification Reports
					</Typography>

					<Typography
						sx={{
							fontSize: 14,
							color: '#6b8888',
						}}>
						Select a template — resident data pre-filled from
						profiles, review before generating
					</Typography>
				</Box>

				<Grid container spacing={2}>
					{/* Left */}
					<Grid container width={'100%'}>
						<Card
							sx={{
								borderRadius: '18px',
								border: '1px solid #d8e8e8',
								boxShadow: 'none',
								width: '100%',
							}}>
							<CardContent sx={{ p: 3 }}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										mb: 3,
									}}>
									<DescriptionRoundedIcon
										sx={{
											fontSize: 18,
											color: teal,
										}}
									/>

									<Typography
										sx={{
											fontSize: 14,
											fontWeight: 700,
											color: dark,
										}}>
										Report Templates
									</Typography>
								</Box>

								<Stack spacing={1}>
									{templates.map(item => (
										<Box
											key={item.title}
											onClick={() =>
												onChangeSelection(null, item)
											}
											sx={{
												pointerEvents:
													onShowTemplateResidentOpt
														? 'none'
														: 'auto',
												opacity:
													onShowTemplateResidentOpt
														? 0.5
														: 1,
												border:
													selectedTemplate === item.id
														? '1px solid #b3ede8'
														: '1px solid #d8e8e8',
												background:
													selectedTemplate === item.id
														? '#eefcf9'
														: '#fff',
												borderRadius: '12px',
												p: 1.5,
												display: 'flex',
												alignItems: 'center',
												gap: 1.5,
												cursor: 'pointer',
												transition: '0.2s',
												'&:hover': {
													borderColor: teal,
													background: '#eefcf9',
												},
											}}>
											<Box
												sx={{
													width: 36,
													height: 36,
													borderRadius: '10px',
													background:
														selectedTemplate ===
														item.id
															? 'rgba(0,196,180,0.15)'
															: surface,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													color:
														selectedTemplate ===
														item.id
															? teal
															: '#6b8888',
												}}>
												{item.icon}
											</Box>

											<Box sx={{ flex: 1 }}>
												<Typography
													sx={{
														fontSize: 13,
														fontWeight: 600,
														color: '#1a2424',
													}}>
													{item.title}
												</Typography>

												<Typography
													sx={{
														fontSize: 11,
														color: '#6b8888',
													}}>
													{item.desc}
												</Typography>
											</Box>

											{selectedTemplate === item.id && (
												<CheckCircleRoundedIcon
													sx={{
														fontSize: 18,
														color: teal,
													}}
												/>
											)}
										</Box>
									))}
								</Stack>
							</CardContent>
						</Card>
					</Grid>

					<Grid container>
						{onShowTemplateResidentOpt && (
							<Card
								sx={{
									borderRadius: '18px',
									border: '1px solid #d8e8e8',
									boxShadow: 'none',
									width: '100%',
									marginTop: '10px',
								}}>
								<CardContent sx={{ p: 3 }}>
									{/* Header */}
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											mb: 2,
										}}>
										<WarningAmberRoundedIcon
											sx={{
												fontSize: 18,
												color: teal,
											}}
										/>

										<Typography
											sx={{
												fontSize: 14,
												fontWeight: 700,
												color: dark,
											}}>
											{`${selectedTemplateName} Notification`}
										</Typography>
									</Box>

									{/* Fields */}
									<Grid container>
										<Typography
											sx={{
												fontSize: 12,
												fontWeight: 600,
												color: '#6b8888',
												mb: 0.7,
											}}>
											Resident
										</Typography>
										<Select
											fullWidth
											size="small"
											disabled={onFetchedPreFilledData}
											// value={id}
											onChange={e =>
												onChangeSelectionElder(
													e.target.value,
												)
											}
											defaultValue="Select Resident"
											error={idError.status}
											sx={{
												fontSize: 13,
												borderRadius: '10px',
												background: '#fff',
											}}>
											<MenuItem
												disabled
												sx={{ fontSize: 12 }}
												value={'Select Resident'}>
												Select Resident
											</MenuItem>
											{residentIds.map((item, index) => (
												<MenuItem
													key={index}
													value={item}
													sx={{ fontSize: '12px' }}>
													{item}
												</MenuItem>
											))}
										</Select>
										<Typography style={helperText}>
											{idError.msg}
										</Typography>
									</Grid>

									<Grid>
										<Grid
											container
											sx={{
												backgroundColor: '#FAFAFA',
												marginBottom: '8px',
												padding: '16px 8px 16px',
												flexDirection: 'column',
												display: 'flex',
											}}>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													gap: 1,
												}}>
												<Typography
													component="div"
													sx={{
														fontSize: '13px',
														color: '#324054',
														fontWeight: 500,
													}}>
													{tempInstructions}
												</Typography>
											</Box>
										</Grid>
										<TextField
											fullWidth
											multiline
											minRows={3}
											value={instructions}
											onChange={e => {
												setInstructions(e.target.value);
												setInstructionError(
													initialErrorState,
												);
											}}
											disabled={onFetchedPreFilledData}
											placeholder="e.g. Give me a brief summary of all residents with dementia who had any incidents in the last 2 weeks, including what happened and what actions were taken…"
											sx={{
												mb: 1.2,
												'& .MuiOutlinedInput-root': {
													borderRadius: '5px',
													backgroundColor: '#fff',
													alignItems: 'flex-start',
													pr: 1,
												},

												'& textarea': {
													fontSize: '13px',
													lineHeight: 1.6,
													color: '#334155',
												},
											}}
											error={instructionError.status}
										/>
										{instructionError.status && (
											<Typography sx={helperText}>
												{instructionError.msg}
											</Typography>
										)}
										<Button
											onClick={getReportData}
											disabled={onFetchedPreFilledData}
											sx={{
												py: 1.2,
												px: 2,
												borderRadius: '10px',
												background: teal,
												color: dark,
												fontSize: 13,
												fontWeight: 600,
												textTransform: 'none',
												boxShadow: 'none',
												'&:hover': {
													background: '#00b0a0',
													boxShadow: 'none',
												},
												marginTop: '5px',
												paddingX: '20px',
											}}>
											Prepare Report
										</Button>
									</Grid>

									<Grid
										container
										spacing={2}
										marginTop={'5px'}>
										{onRenderFields()}
									</Grid>
									{Object.keys(reportData).length > 0 && (
										<Grid container>
											<Button
												startIcon={<AutoAwesomeIcon />}
												onClick={e =>
													setAnchorEl(e.currentTarget)
												}
												disabled={isLoading}
												sx={{
													mt: '10px',
													py: 1.2,
													px: 2,
													borderRadius: 2,
													textTransform: 'none',
													background: teal,
													color: '#083838',
													fontWeight: 600,
													fontSize: 13,
													boxShadow: 'none',
													'&:hover': {
														background: '#00b2a3',
														boxShadow: 'none',
													},
												}}>
												Generate Report
											</Button>
											<Menu
												anchorEl={anchorEl}
												open={Boolean(anchorEl)}
												onClose={handleClose}
												anchorOrigin={{
													vertical: 'bottom',
													horizontal: 'right',
												}}
												transformOrigin={{
													vertical: 'top',
													horizontal: 'right',
												}}
												PaperProps={{
													sx: {
														mt: 0,
														width: '180px',
													},
												}}>
												<MenuItem
													sx={{ fontSize: '14px' }}
													onClick={() =>
														onFetchTemplateData(
															'pdf',
														)
													}>
													Generate (.PDF)
												</MenuItem>
												<MenuItem
													sx={{ fontSize: '14px' }}
													onClick={() =>
														onFetchTemplateData(
															'docx',
														)
													}>
													Generate (.DOCX)
												</MenuItem>
												<MenuItem
													sx={{ fontSize: '14px' }}
													onClick={
														generateBothTempData
													}>
													Generate Both
												</MenuItem>
											</Menu>
										</Grid>
									)}
								</CardContent>
							</Card>
						)}
					</Grid>
				</Grid>
			</Box>
			{isLoading && (
				<ToastBanner
					open={isLoading}
					message={loadingMsg}
					icon={<StarIcon color={'#FFFFFF'} />}
					isCompleted={isGenerationComplete}
				/>
			)}
		</MainGrid>
	);
}
